import { useEffect, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/i18n";
import { CmykMark } from "@/components/Brand";

const GoogleButton = ({ label }) => {
  const go = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/painel";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <Button type="button" variant="outline" onClick={go} data-testid="google-login-button" className="h-12 w-full rounded-full border-slate-300 text-base">
      <svg className="mr-2 h-5 w-5" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z"/></svg>
      {label}
    </Button>
  );
};

export default function AuthPage() {
  const [params] = useSearchParams();
  const { user, setUser } = useAuth();
  const { t, tErr } = useLang();
  const [showEmail, setShowEmail] = useState(false);
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const next = params.get("next") || "/painel";
  const reg = params.get("modo") === "cadastro";

  useEffect(() => { if (params.get("erro") === "google") toast.error(t("auth.googleError")); }, [params, t]);
  if (user) return <Navigate to={next} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", form);
      setUser(data);
      toast.success(t("auth.welcomeBack"));
    } catch (err) {
      toast.error(tErr(errMsg(err)));
    } finally {
      setLoading(false);
    }
  };
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div className="grid-paper flex min-h-[calc(100vh-148px)] items-center justify-center px-4 py-16">
      <div className="rise w-full max-w-md rounded-2xl border border-slate-200 bg-white p-8 shadow-xl shadow-slate-900/5 sm:p-10" data-testid="auth-card">
        <CmykMark />
        <h1 className="mt-5 text-3xl font-bold tracking-tight text-slate-900">{reg ? t("auth.registerTitle") : t("auth.loginTitle")}</h1>
        <p className="mt-2 text-sm text-slate-500">{reg ? t("auth.registerSub") : t("auth.loginSub")}</p>
        <div className="mt-6 flex items-center gap-4 rounded-xl bg-[#FFB800]/15 p-4" data-testid="auth-free-callout">
          <span className="grid h-12 w-12 shrink-0 place-items-center rounded-full bg-[#FFB800] font-display text-2xl font-bold text-slate-900">5</span>
          <div>
            <p className="text-sm font-bold text-slate-900">{t("free.authTitle")}</p>
            <p className="text-xs text-slate-600">{t("free.authText")}</p>
          </div>
        </div>
        <div className="mt-6"><GoogleButton label={t("auth.google")} /></div>
        <p className="mt-3 text-center text-xs text-slate-400">{t("auth.googleOnlyNote")}</p>
        {showEmail ? (
          <>
            <div className="my-6 flex items-center gap-3 text-xs text-slate-400"><span className="h-px flex-1 bg-slate-200" />{t("auth.orEmail")}<span className="h-px flex-1 bg-slate-200" /></div>
            <form onSubmit={submit} className="space-y-4">
              <div><Label htmlFor="email">{t("auth.email")}</Label><Input id="email" type="email" required value={form.email} onChange={set("email")} className="mt-1.5 h-11" data-testid="auth-email-input" /></div>
              <div><Label htmlFor="password">{t("auth.password")}</Label><Input id="password" type="password" required value={form.password} onChange={set("password")} className="mt-1.5 h-11" data-testid="auth-password-input" /></div>
              <Button type="submit" disabled={loading} className="h-12 w-full rounded-full bg-slate-900 text-base hover:bg-slate-800" data-testid="auth-submit-button">
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{t("auth.loginBtn")}
              </Button>
            </form>
          </>
        ) : (
          <button onClick={() => setShowEmail(true)} className="mt-8 block w-full text-center text-xs text-slate-400 hover:text-slate-700" data-testid="auth-show-email-login">
            {t("auth.adminLogin")}
          </button>
        )}
      </div>
    </div>
  );
}
